import React, { useState, useEffect } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { functionRegistry } from '../../utils/functionRegistry';
import EpsilonEditor from './EpsilonEditor';
import FunctionSymbol from './FunctionSymbol';
import OperandSlot from './OperandSlot';
import type { ExpressionNode } from '../../state/types';

type Props = {
  id: string;
  functionId: string;
  operandIds: (string | null)[];
  expressions: Record<string, ExpressionNode>;
  childElements?: (React.ReactNode | null)[];
  /** Max estimated operand-row width before switching to vertical layout (from canvas column ResizeObserver). */
  layoutBudgetPx?: number;
  onRemoveOperand?: (parentId: string, index: number) => void;
  onInsertLiteral?: (parentId: string, index: number, value: number | string, isString?: boolean) => void;
  onUpdateEpsilon?: (expressionId: string, epsilon: number) => void;
  onRequestEditSeries?: (args: {
    expressionId: string;
    slotIndex: number;
    operandId: string;
  }) => void;
  rootRef: React.RefObject<HTMLDivElement | null>;
  isHoveredDuringDrag?: boolean;
  isDragging?: boolean;
};

const ExpressionBlock: React.FC<Props> = ({
  id,
  functionId,
  operandIds,
  expressions,
  layoutBudgetPx,
  onRemoveOperand,
  onInsertLiteral,
  onUpdateEpsilon,
  onRequestEditSeries,
  rootRef: _rootRef,
  isHoveredDuringDrag,
  isDragging: _isDragging,
  childElements,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `function-${id}`,
    data: { type: 'function-block' },
  });

  const isHovered = (isHoveredDuringDrag ?? false) || isOver;

  // SINGLE REGISTRY LOOKUP – legacy is gone forever
  const def = functionRegistry.get(functionId) ?? {
    symbol: functionId,
    renderStyle: 'prefix' as const,
    parameters: [],
    operandLabels: [],
    minParameters: 2,  // safe explicit default for unknown function
    maxParameters: 2,  // safe explicit default for unknown function
  };

  const {
    symbol,
    renderStyle = 'prefix',
    parameters: _parameters = [],
    operandLabels = [],
    minParameters,
    maxParameters,
  } = def;

  // Arity calculation – clean, explicit, future-proof
  const minArgs = minParameters;
  const maxArgs = maxParameters; // null stays null → unlimited

  // === FULLY PRESERVED MULTI-ARITY LOGIC ===
  const filledSlots = operandIds.filter(id => id !== null).length;
  const hasRoomForMore = (maxArgs === null || filledSlots < maxArgs) && filledSlots >= minArgs;

  const filledCount = operandIds.filter(Boolean).length;
  // For seriesAware/seriesAwareWindow-only functions, cap display at 1 slot per param (avoid expanded 4 slots)
  const isSeriesAwareOnly = def.parameters.length > 0 && def.parameters.every(
    (p: { type: string }) => p.type === 'seriesAware' || p.type === 'seriesAwareWindow'
  );
  const baseSlotCount = isSeriesAwareOnly
    ? Math.min(Math.max(filledCount, minArgs), def.parameters.length)
    : maxArgs === null
      ? Math.max(filledCount, minArgs, operandIds.length)
      : Math.max(filledCount, minArgs);
  const totalSlots = isHovered && hasRoomForMore ? baseSlotCount + 1 : baseSlotCount;

  const effectiveRenderStyle =
    renderStyle === 'arithmetic' ? (totalSlots === 2 ? 'infix' : 'prefix') : renderStyle;

  const [editingSlotIndex, setEditingSlotIndex] = useState<number | null>(null);
  const [literalInput, setLiteralInput] = useState('');

  const handleLiteralSubmit = (index: number, inputValue: string) => {
    const parentNode = expressions[id];
    const parentDef = functionRegistry.get(functionId);
    const expectedTypeFromParent =
      parentDef?.returnType === 'string' && parentDef?.enumValues?.length
        ? 'string'
        : null;

    const expectedType =
      expectedTypeFromParent ??
      (parentNode?.type === 'function' && parentNode.operandIds[0]
        ? (() => {
            const firstOperand = expressions[parentNode.operandIds[0]];
            if (firstOperand?.type === 'function') {
              return functionRegistry.get(firstOperand.functionId)?.returnType ?? 'number';
            }
            return 'number';
          })()
        : 'number');

    if (expectedType === 'number') {
      const value = parseFloat(inputValue);
      if (isNaN(value)) return;
      onInsertLiteral?.(id, index, value);
    } else if (expectedType === 'string') {
      const trimmed = inputValue.trim();
      if (!trimmed) return;
      onInsertLiteral?.(id, index, trimmed, true);
    }

    setEditingSlotIndex(null);
    setLiteralInput(''); // reset state
  };

  const handleCancelEdit = () => {
    setEditingSlotIndex(null);
    setLiteralInput('');
  };

  /**
   * Width heuristic for horizontal vs vertical layout.
   * Previously used 100px leaves and ignored function symbols; OperandDropZone is min-w-[120px]
   * and series pills are often wider — so blocks stayed "horizontal" while the DOM overflowed and
   * clipped the palettes. We add per-function prefix overhead and conservative leaf width, then
   * compare to `layoutBudgetPx` from the actual canvas column width.
   */
  const estimateOperandRowWidth = (
    ids: (string | null)[],
    ex: Record<string, any>,
    leafWidth: number,
    gap: number,
    functionPrefix: number
  ): number => {
    const filled = ids.filter((x): x is string => x != null);
    if (filled.length === 0) return leafWidth;
    let sum = 0;
    filled.forEach((opId) => {
      const child = ex[opId];
      if (child && child.type === 'function') {
        const inner = estimateOperandRowWidth(child.operandIds, ex, leafWidth, gap, functionPrefix);
        sum += functionPrefix + inner;
      } else {
        sum += leafWidth;
      }
    });
    return sum + (filled.length - 1) * gap;
  };

  /** OperandDropZone min-w-[120px] + padding; series/indicator labels often exceed 120px. */
  const leafEstimatePx = 168;
  const operandGapPx = 8;
  /** Function label (e.g. ROC, AND) + gap before operand row in prefix layout. */
  const functionPrefixPx = 52;
  const maxCanvasWidth = layoutBudgetPx ?? 400;

  const [layout, setLayout] = useState<'horizontal' | 'vertical'>('horizontal');
  useEffect(() => {
    const operandRow = estimateOperandRowWidth(
      operandIds,
      expressions,
      leafEstimatePx,
      operandGapPx,
      functionPrefixPx
    );
    const prefixOverhead = effectiveRenderStyle === 'infix' ? 0 : functionPrefixPx;
    const totalWidth = prefixOverhead + operandRow;
    setLayout(totalWidth > maxCanvasWidth ? 'vertical' : 'horizontal');
  }, [operandIds, expressions, maxCanvasWidth, effectiveRenderStyle]);

  return (
    <div
      ref={setNodeRef}
      className="flex items-center gap-2 border border-rule rounded-sm p-2 bg-newsprint-dark min-w-0 w-full max-w-full relative"
      style={{ pointerEvents: 'auto' }}
      onDragOver={(e) => e.preventDefault()}
    >
      {/* PREFIX RENDER STYLE */}
      {effectiveRenderStyle === 'prefix' && (
        <div
          className={
            layout === 'horizontal'
              ? 'flex max-w-full min-w-0 flex-row items-start gap-2'
              : 'flex w-full min-w-0 max-w-full flex-col gap-1'
          }
        >
          <div className="shrink-0 pt-0.5">
            <FunctionSymbol symbol={symbol} />
          </div>
          {/* Horizontal: operands wrap in their own cluster so wrapped rows share one left edge
              (symbol stays out of the flex-wrap row — avoids staggered slots). */}
          <div
            className={
              layout === 'horizontal'
                ? 'flex min-w-0 flex-1 flex-row flex-wrap gap-2'
                : 'flex w-full min-w-0 max-w-full flex-col gap-1'
            }
          >
            {Array.from({ length: totalSlots }, (_, i) => {
              const isExtra = i >= baseSlotCount;

              return (
                <div
                  key={`${id}-slot-${i}`}
                  className="min-w-0 max-w-full"
                  style={{
                    marginLeft: layout === 'vertical' ? '1em' : 0,
                    display: 'inline-flex',
                    flexDirection: layout === 'vertical' ? 'column' : 'row',
                  }}
                >
                  <OperandSlot
                    index={i}
                    expressionId={id}
                    operandId={operandIds[i] ?? undefined}
                    label={operandLabels[i]}
                    expressions={expressions}
                    isEditing={editingSlotIndex === i}
                    literalInput={literalInput}
                    onLiteralChange={setLiteralInput}
                    onLiteralSubmit={(idx, val) => handleLiteralSubmit(idx, val)}
                    onCancelEdit={handleCancelEdit}
                    onRemove={isExtra ? undefined : () => onRemoveOperand?.(id, i)}
                    onClick={!operandIds[i] && editingSlotIndex !== i ? () => {
                      setEditingSlotIndex(i);
                      setLiteralInput('');
                    } : undefined}
                    renderedChildExpression={childElements?.[i] ?? undefined}
                    onEditSeries={operandIds[i] ? () => onRequestEditSeries?.({
                      expressionId: id,
                      slotIndex: i,
                      operandId: operandIds[i]!,
                    }) : undefined}
                    isExtraSlot={isExtra}
                    isHovered={isHovered}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* INFIX RENDER STYLE */}
      {effectiveRenderStyle === 'infix' && (
        <div
          className={
            layout === 'vertical'
              ? 'flex flex-col gap-2 items-stretch min-w-0 w-full'
              : 'flex flex-row gap-2 items-center flex-nowrap min-w-0 w-full'
          }
        >
          {Array.from({ length: totalSlots }, (_, i) => {
            const isExtra = i >= baseSlotCount;

            return (
              <React.Fragment key={`${id}-slot-${i}`}>
                <OperandSlot
                  index={i}
                  expressionId={id}
                  operandId={operandIds[i] ?? undefined}
                  label={operandLabels[i]}
                  expressions={expressions}
                  isEditing={editingSlotIndex === i}
                  literalInput={literalInput}
                  onLiteralChange={setLiteralInput}
                  onLiteralSubmit={(idx, val) => handleLiteralSubmit(idx, val)}
                  onCancelEdit={handleCancelEdit}
                  onRemove={isExtra ? undefined : () => onRemoveOperand?.(id, i)}
                  onClick={!operandIds[i] && editingSlotIndex !== i ? () => {
                    setEditingSlotIndex(i);
                    setLiteralInput('');
                  } : undefined}
                  renderedChildExpression={childElements?.[i] ?? undefined}
                  onEditSeries={operandIds[i] ? () => onRequestEditSeries?.({
                    expressionId: id,
                    slotIndex: i,
                    operandId: operandIds[i]!,
                  }) : undefined}
                  isExtraSlot={isExtra}
                  isHovered={isHovered}
                />
                {i < totalSlots - 1 && <FunctionSymbol symbol={symbol} />}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Epsilon for = / != */}
      {(functionId === '=' || functionId === '!=') && onUpdateEpsilon && (
        <EpsilonEditor
          expressionId={id}
          currentEpsilon={expressions[id]?.type === 'function' ? expressions[id].epsilon : undefined}
          onUpdateEpsilon={onUpdateEpsilon}
        />
      )}
    </div>
  );
};

export default ExpressionBlock;